import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useFinalSubmit } from "~/composables/useFinalSubmit";

export const useEmployeeSession = defineStore("employeeSession", () => {
  const session = ref(null);
  const isClosing = ref(false);

  const isAuthenticated = computed(() => !!session.value);

  const canStoreGenericDiscountLines = computed(() => {
    return session.value?.employee_can_store_generic_discount_lines ?? false;
  });

  /**
   * Syncs the session state from an external source (like the initial auth load).
   */
  function setSession(sessionData) {
    session.value = sessionData;
  }

  /**
   * Starts a new employee session using a PIN code.
   */
  async function start(pinCode) {
    const { submit } = useFinalSubmit();

    const response = await submit("/employee-sessions", {
      body: {
        pin_code: pinCode,
      },
    });

    if (response.ok && response.data) {
      session.value = {
        id: response.data.id,
        employee_first_name: response.data.attributes.employee_first_name,
        employee_last_name: response.data.attributes.employee_last_name,
        employee_can_store_generic_discount_lines:
          response.data.attributes.employee_can_store_generic_discount_lines,
        employee_id: response.data.attributes.employee_id,
      };
    }

    return response;
  }

  /**
   * Closes the current employee session.
   */
  async function close() {
    const { submit } = useFinalSubmit();

    if (!session.value || isClosing.value) {
      return;
    }

    isClosing.value = true;

    await submit("/employee-session/close");

    session.value = null;

    await navigateTo("/login");

    isClosing.value = false;
  }

  return {
    session,

    isAuthenticated,
    canStoreGenericDiscountLines,

    setSession,
    start,
    close,
    isClosing,
  };
});
